import { atom } from 'jotai';

import {
  CameraTargetProps,
  CameraType,
  CurrentImageProps,
  Number3,
  TransformMode,
  VisibilityBoxProps,
} from '@fabric-web/3d-viewer';
import {
  Zone,
  WorkflowStages,
  ZoneStatusCount,
  StructureDeploymentLevel,
} from '@/__generated__/graphql';
import {
  GetStructureRelationshipsForTaggingInspectionDataLoaderQuery,
  GetStructureLocationsForTaggingDataLoaderQuery,
} from '@/__generated__/graphql';
import { History } from '@/__generated__/graphql';

import {
  IsolatedPartVisibilityRangeType,
  NormalVisibilityRange,
  PanelsMode,
  SnackbarMessage,
  SphericalPointCloudMode,
  SphericalPointCloudState,
  StructureRelationships,
  AbyssViewerState,
  LabellingStructureRelationships,
  LabellingBlendingMode,
  LabellingAnnotationMode,
  AllStructures,
  CuboidClickMode,
  PointSize,
  StyledSplitCuboid,
  LabellingCuboid,
  RiskRemediationTimeIntervalEnum,
} from '@/types';

import { DEFAULT_VISIBILITY_BOX } from '@/constants';
// import {
//   getSessionStorageValue,
//   removeSessionStorageItem,
//   setSessionStorageValue,
// } from '@/utils/sessionLocalStorage';
import { atomWithReset } from 'jotai/utils';

export const auth0TokenState = atom<string | undefined>(undefined);

export const userSyncedState = atom<boolean>(false);
export const structures = atom<AllStructures>([]);

export const selectedStructureId = atom<string | undefined>(undefined);

export const selectedPlatformId = atom<string | undefined>(undefined);

export const selectedStructureDeploymentLevel = atom<StructureDeploymentLevel | undefined>(
  undefined
);

export const inspectionMetadata = atom<
  GetStructureRelationshipsForTaggingInspectionDataLoaderQuery['structure'] | undefined
>(undefined);

export const structureLocations = atom<
  GetStructureLocationsForTaggingDataLoaderQuery['allLocations'] | undefined
>(undefined);

export const viewerCurrentSpherical = atom<CurrentImageProps | undefined>(undefined);

export const viewerNextCurrentSpherical = atom<CurrentImageProps | undefined>(undefined);

export const searchedSphericalId = atom<string | undefined>(undefined);

export const areSphericalsVisible = atom<boolean>(true);

export const structureRelationships = atom<StructureRelationships | undefined>(undefined);

export const selectedPartIds = atom<string[]>([]);

export const snackbarMessage = atom<SnackbarMessage>({ shouldShow: false });

export const reportDownloadSnackbarMessage = atom<SnackbarMessage>({ shouldShow: false });

export const searchedZone = atom<string>('');

export const currentVisibilityBox = atom<VisibilityBoxProps>(DEFAULT_VISIBILITY_BOX);

export const cameraTarget = atom<CameraTargetProps | undefined>(undefined);

export const abyssViewerState = atomWithReset<AbyssViewerState>({ mode: 'Normal' });

export const sphericalPointCloudState = atom<SphericalPointCloudState>('Loaded');

export const sphericalPointCloudMode = atom<SphericalPointCloudMode>('All');

/**
 * @deprecated This atom is strictly deprecated and should **NOT** be used.
 *
 * This atom must not be accessed directly. There's a selector called `pointSize` that should be used instead.
 *
 * This is due to the fact that this atom is used to manage poitSizes for different viewer modes.
 *
 * And the selector `pointSize` contains logic that automically determines the correct pointSize based on the current viewer mode.
 *
 * Setting this atom directly **might** cause unexpected behavior.
 */
export const pointSizeAtom = atom<PointSize>({
  viewer: 1,
  spherical: 4,
});

export const dynamicPointSize = atom<number>(1);

export const enableDynamicPointSize = atom<boolean>(false);

export const edlStrength = atom<number>(1);

export const selectedZone = atom<Zone | null>(null);

export const selectedZoneDetails = atom<Zone | undefined>(undefined);

export const startTimeStampForTimeLogs = atom<Date>(new Date());

export const selectedAnnotationIds = atom<string[]>([]);

export const isolatedPartVisibilityRange = atom<IsolatedPartVisibilityRangeType>('All');

export const normalVisibilityRange = atom<NormalVisibilityRange>('All');

// Have this as a separate part of state to abyss viewer mode,
// otherwise InspectorExplorer re-renders whenever abyss viewer mode change
// and the way rc-dock controlled mode seems to work (or how we are using it) means that
// a re-render re-mounts panel content
// (looks like react reconciliation can't figure out e.g ZonePanel is the same
// when switching between Normal and Spherical mode)
// TODO:
// revisit this atom later, current `Default` is used as default panel for all workflows
// we might need to have different modes for different workflow
// eg: `Tagging` when start tagging, `Finder` when start analysis
export const panelsMode = atomWithReset<PanelsMode>('Default');

export const shouldHighlightReviewedParts = atom<boolean>(false);

export const shouldHighlightAllPartsWithSameTag = atom<boolean>(false);

export const searchedHighlightedTag = atom<{
  id: string;
  tagName: string;
} | null>(null);

export const zones = atom<Zone[]>([]);

export const shouldShowDemarcationOfZones = atom<boolean>(false);

export const zoneSegmentsHeight = atom<number>(0);

export const zonesSearchTerm = atom<string>('');

export const selectedSuperVoxels = atom<number[]>([]);

export const labellingStructureRelationships = atom<LabellingStructureRelationships | undefined>(
  undefined
);

// shared global state that can be reused in tagging/analysis/labelling
// used for increasing or decreasing brush size of cursor
export const brushSize = atom<number>(0);

export const labellingCurrentAnnotationMode = atom<LabellingAnnotationMode>(
  LabellingAnnotationMode.SUPERVOXEL
);

export const labellingShouldShowBlendingModes = atom<boolean>(false);

export const labellingCurrentBlendingMode = atom<LabellingBlendingMode>(
  LabellingBlendingMode.SUPERVOXEL
);

export const blendingModes = atom<Array<{ id: LabellingBlendingMode; text: string }>>([
  {
    id: LabellingBlendingMode.SUPERVOXEL,
    text: 'Annotation Colors',
  },
  {
    id: LabellingBlendingMode.SUPERVOXEL_RGB,
    text: 'RGB With Annotation Colors',
  },
  {
    id: LabellingBlendingMode.COLOUR,
    text: 'RGB Colors',
  },
]);

export const annotationModes = atom<Array<{ id: LabellingAnnotationMode; text: string }>>([
  {
    id: LabellingAnnotationMode.SUPERVOXEL,
    text: 'Super voxel id',
  },
  {
    id: LabellingAnnotationMode.ASSET_TYPE,
    text: 'Asset type id',
  },
]);

export const shouldDeselectAfterSetsupervoxelClass = atom<boolean>(false);

export const displayAnimationEditor = atom<boolean>(false);

export const zoneHistories = atom<Array<{ id: string; histories: Array<History> }>>([]);
// direction of the camera for the assembly screenshot
export const cameraDirection = atom<Number3 | undefined>(undefined);

export const cameraType = atom<CameraType>(CameraType.Perspective);

export const enableAdaptivePointBudget = atom<boolean>(true);

export const pointBudget = atom<number>(5_000_000);

export const rgbBlendLevel = atom<number>(1);

export const isStructureCorrosionReady = atom<boolean>(false);

export const statesLoaded = atom<boolean>(false);

export const isSubscriptionsEnabled = atom<boolean>(true);

export const areUnselectedEquipmentHidden = atom<boolean>(false);

export const hideTaggedPoints = atom<boolean>(false);

export const isSystemPopoverOpened = atom<boolean>(false);

export const canDisplaySystemPopover = atom<boolean>(true);
// export const canDisplaySystemPopover = atom<boolean>(true, (get, set, newValue) => {
//   const storageKey = 'CanDisplaySystemPopover';
//   const savedValue = getSessionStorageValue(storageKey, true);
//   set(newValue);

//   if (newValue === RESET) {
//     removeSessionStorageItem(storageKey);
//   } else {
//     setSessionStorageValue(storageKey, newValue);
//   }
// });

export const cuboidTransformMode = atom<TransformMode>(TransformMode.BoxResize);

export const currentCuboid = atom<StyledSplitCuboid | undefined>(undefined);

export const cuboidClickMode = atom<CuboidClickMode>(CuboidClickMode.Default);

export const isCuboidEditorEnabled = atom<boolean>(false);

export const isCuboidVisibilityBoxEnabled = atom<boolean>(true);

export type PlatformDisplay =
  | 'coating'
  | 'coating-health'
  | 'health'
  | 'criticality'
  | 'risk score';

export const platformSelectedFilter = atom<PlatformDisplay>('coating');

export const platformsFilters = atom<PlatformDisplay[]>([]);

export const platformName = atom<string>('');

export const brightness = atom<number>(0);

export const contrast = atom<number>(0);

export const workflowStages = atom<WorkflowStages[]>([]);

export const currentWorkflowStage = atom<WorkflowStages | undefined>(undefined);

export const zoneStatusCounts = atom<ZoneStatusCount[]>([]);

export const referenceCuboid = atom<LabellingCuboid | undefined>(undefined);

export const showZoneDetailModal = atom<boolean>(false);

export const isAddingWorkscope = atom<boolean>(false);

export const hasRiskRemediationData = atom<boolean>(false);

export const paintBlockReportPoiTemplateTypes = atom<string[]>([]);

export const riskRemediationTimeInterval = atom<RiskRemediationTimeIntervalEnum>(
  RiskRemediationTimeIntervalEnum.MONTHLY
);
